// File: MapView.tsx

import React, { FC, Dispatch, SetStateAction, useCallback, useMemo } from "react"; // Import useMemo
import DeckGL from "@deck.gl/react";
import { ViewStateType } from "@/types/BigDataMap.types";
// Import necessary types from DeckGL core and mjolnir
import { PickingInfo, ViewStateChangeParameters, TransitionInterpolator, Layer } from "@deck.gl/core";
import { MjolnirGestureEvent } from "mjolnir.js";

// Define props for the MapView component
interface MapViewProps {
  viewState: ViewStateType; // The "clean" state from the parent
  setViewState: Dispatch<SetStateAction<ViewStateType>>; // Setter for the "clean" state
  layers: Layer<any>[];
  handleCanvasClick: (info: PickingInfo, event: MjolnirGestureEvent) => void;
  // Separate props for transition control
  transitionDuration?: number | "auto";
  transitionInterpolator?: TransitionInterpolator;
  isResetting?: boolean;
}

const MapView: FC<MapViewProps> = ({
  viewState,
  setViewState,
  layers,
  handleCanvasClick,
  // Destructure transition props
  transitionDuration = 0, // Default to no transition if not provided
  transitionInterpolator = undefined,
  isResetting = false,
}) => {
  const effectiveTransitionDuration = isResetting ? 0 : transitionDuration;
  // Refined handler for view state changes from user interaction
  const handleViewStateChange = useCallback(
    (evt: ViewStateChangeParameters) => {
      const { longitude, latitude, zoom, pitch, bearing } = evt.viewState;
      // Construct the "clean" state update without transition props
      const nextState: ViewStateType = {
        longitude,
        latitude,
        zoom,
        pitch,
        bearing,
        minZoom: viewState.minZoom,
        maxZoom: viewState.maxZoom,
      };
      // Update the parent's "clean" state
      setViewState(nextState);
    },
    [setViewState, viewState.minZoom, viewState.maxZoom]
  );

  // --- Combine viewState with transition props for DeckGL ---
  // useMemo prevents recreating this object on every render unless dependencies change
  const deckGLViewState = useMemo(
    () => ({
      ...viewState, // Spread the clean state properties
      // Add the transition properties for DeckGL to use
      transitionDuration: effectiveTransitionDuration,
      transitionInterpolator: transitionInterpolator,
    }),
    [viewState, effectiveTransitionDuration, transitionInterpolator]
  ); // Recalculate if these change

  return (
    <div className="h-full w-full relative overflow-hidden">
      <DeckGL
        layers={layers}
        // --- Pass the COMBINED view state object ---
        viewState={deckGLViewState}
        // --- End Pass Combined ---
        onViewStateChange={handleViewStateChange} // This updates the CLEAN parent state
        controller={true}
        onClick={handleCanvasClick}
        getCursor={() => "default"}
        // REMOVED transitionDuration / transitionInterpolator props from here
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100%",
        }}
      />
    </div>
  );
};

export default React.memo(MapView);
